import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import { string, object } from 'yup';
import {
  AccountTypes,
  AssociationAccount,
  AssociationContact,
  AssociationsResponse,
  OpportunityDetailModel,
  OpportunityType,
  PlanData,
} from '@apparatix/types/Leads';
import { useOpportunityStore } from './useOpportunityStore';
import {
  useAccounts,
  useCreateOpportunity,
  useEndpoint,
  useOpportunities,
  useOpportunityAssociations,
} from '@apparatix/composables';

type StepIndex = 1 | 2 | 3 | 4;
type StepNames = 'name' | 'accounts' | 'contacts' | 'details';
type ChangeTracker = {
  name: string;
  accountIds: number[];
  contactIds: number[];
}

export type Steps = Record<StepNames, StepIndex>;

export const useOpportunityCreationStore = defineStore('opportunity-creation', () => {

  const currentStep = ref<StepIndex>(1);
  const steps = ref<Steps>({
    name: 1,
    accounts: 2,
    contacts: 3,
    details: 4,
  });

  const accounts = ref<AssociationAccount[]>([]);
  const contacts = ref<AssociationContact[]>([]);

  const type = ref<OpportunityType>();
  const detailComponentRef = ref();
  const isLoading = ref(false);
  const showFinishScreen = ref(false);
  const notes = ref<string>();
  const planData = ref<PlanData>({
    solutionsAndStrategy: undefined,
    statedNeeds: undefined,
    campaignObjective: undefined,
    miscNotes: undefined,
  });

  const initialValues = ref<ChangeTracker>({
    name: undefined,
    accountIds: [],
    contactIds: [],
  });

  const opportunityStore = useOpportunityStore();
  const { getContactsByAdvertiser } = useAccounts();
  const { handleSave } = useCreateOpportunity(type.value, accounts, contacts);
  const { onAfterLeadConverted } = useOpportunities();

  const associationEndpoint = useEndpoint<AssociationsResponse>({
    type: 'GET',
    url: '',
    successField: 'data',
    onSuccess: response => {
      if (response.data.accounts?.length) {
        accounts.value = response.data.accounts.filter(account => account.c2Status !== 'Inactive');
        initialValues.value.accountIds = response.data.accounts.map(account => account.c2Id);
      }
      if (response.data.contacts?.length) {
        contacts.value = response.data.contacts;
        initialValues.value.contactIds = response.data.contacts.map(contact => contact.id ?? contact.c2Id);
      }
    },
    toast: { showOnSuccess: false },
  });

  const {
    accountTypesValidForSelection, hasAccountType, errorBag,
  } = useOpportunityAssociations(
    type.value,
    accounts,
    contacts,
  );

  const {
    defineField, errors, meta, resetForm,
  } = useForm({
    validationSchema: toTypedSchema(object().shape({ name: string().required('Name is required') })),
    validateOnMount: false,
    keepValuesOnUnmount: false,
  });

  const [name, nameProps] = defineField('name');

  watch(() => opportunityStore.showOpportunityCreationModal, showing => {
    if (showing) return;

    initialValues.value = {
      name: undefined,
      accountIds: [],
      contactIds: [],
    };
  });

  const canGoToNextStep = computed(() => {
    if (currentStep.value === steps.value.name) {
      return !errors.value.name && meta.value.dirty;
    }

    if (currentStep.value === steps.value.accounts) {
      return errorBag.value.accounts.length === 0;
    }

    if (currentStep.value === steps.value.contacts) {
      return errorBag.value.contacts.length === 0;
    }

    return false;
  });

  const typeForDetailEditor = computed<OpportunityType>(() => {
    if (opportunityStore.createOpportunityContext === 'create-lead') {
      return 'Lead';
    }

    return 'Opportunity';
  });

  const canGoBack = computed(() => currentStep.value > steps.value.name);

  const agencyAccountId = computed(() => {
    if (!accounts.value?.length || !hasAccountType(accounts.value, 'Agency')) return null;
    const agency = accounts.value.find(account => account.c2Type === 'Agency');
    if (agency) {
      return agency.c2Id;
    }

    return null;
  });

  const hasReachedMaximumAccounts = computed(() =>
    hasAccountType(accounts.value, 'Advertiser') &&
  hasAccountType(accounts.value, 'Agency') &&
  hasAccountType(accounts.value, 'Bill-To'),
  );

  const isConvertingLead = computed(() => opportunityStore.createOpportunityContext === 'convert-lead');

  const canSkipStep = computed(() => {
    if (currentStep.value === steps.value.name) {
      return false;
    }
    if (currentStep.value === steps.value.accounts) {
      return type.value === 'Lead';
    }
    if (currentStep.value === steps.value.contacts) {
      return type.value === 'Lead';
    }
    return true;
  });

  const canFinish = computed(() => {
    if (showFinishScreen.value) return false;
    return meta.value.valid && errorBag.value.accounts.length === 0 && errorBag.value.contacts.length === 0;
  });

  const hasUnsavedChanges = () => {
    if (initialValues.value.name !== name.value) return true;

    const accountIds = accounts.value.map(account => account.c2Id).sort();
    const contactIds = contacts.value.map(contact => contact.id ?? contact.c2Id).sort();

    return JSON.stringify(accountIds) !== JSON.stringify(initialValues.value.accountIds.sort()) || JSON.stringify(contactIds) !== JSON.stringify(initialValues.value.contactIds.sort());
  };

  const onGotoContacts = async () => {
    if (accounts.value?.length === 0) return;
    isLoading.value = true;

    const importsComplete = accounts.value.map(() => Promise.withResolvers());

    accounts.value.forEach((account, idx) => {
      getContactsByAdvertiser(account.c2Id, results => {
        if (results?.length) {
          results.forEach(result => {
            if (!contacts.value.find(c => c.id === result.id)) {
              contacts.value.push(result);
            }
          });

          contacts.value = removeDuplicateContactLabels(contacts.value);

          if (importsComplete[idx]) {
            importsComplete[idx].resolve(true);
          }
        } else {
          if (importsComplete[idx]) {
            importsComplete[idx].resolve(true);
          }
        }
      });
    });

    await Promise.all([...importsComplete.map(promise => promise.promise)]);
    isLoading.value = false;
  };

  const accountOwnerId = ref(0);

  const addAccount = (account: AssociationAccount) => {
    const idx = accounts.value?.findIndex(act => act.c2Id === account.c2Id);
    if (idx !== -1) return;
    accounts.value.push(account);

    if (account.c2RawType === 'Advertiser') {
      accountOwnerId.value = account.ownerId;
    }
  };

  const removeAccount = (account: AssociationAccount) => {
    const idx = accounts.value?.findIndex(act => act.c2Id === account.c2Id);
    if (idx === -1) return;
    accounts.value.splice(idx, 1);
  };

  const addContact = (contact: AssociationContact) => {
    const idx = contacts.value.findIndex(ct => ct.id === contact.id);
    const primaryLabel = opportunityStore.options['Opportunity'].contactLabels.find(label => label.contactLabelId === 1);
    if (contacts.value.length === 0 && !contact.contactLabels?.length) {
      contact.contactLabels = [primaryLabel];
    }
    if (idx === -1) {
      contacts.value.push(contact);
    } else {
      contacts.value[idx] = contact;
    }
  };

  const removeContact = (contact: AssociationContact) => {
    const idx = contacts.value?.findIndex(ct => ct.id === contact.id);
    if (idx === -1) return;
    contacts.value.splice(idx, 1);
  };

  const removeDuplicateContactLabels = (contactList: AssociationContact[]) => {
    const usedLabels = new Set();
    return contactList.map(contact => {
      if (!contact.contactLabels || !Array.isArray(contact.contactLabels)) {
        return contact;
      }

      const uniqueLabels = contact.contactLabels.filter(label => {
        if (!usedLabels.has(label.contactLabelId)) {
          usedLabels.add(label.contactLabelId);
          return true;
        }
        return false;
      });

      return {
        ...contact,
        contactLabels: uniqueLabels,
      };
    });
  };

  const loadLeadToConvert = async () => {
    if (!opportunityStore.selectedOpportunity['Lead']?.opportunity?.id) return;
    isLoading.value = true;
    name.value = opportunityStore.selectedOpportunity['Lead'].opportunity.name;
    initialValues.value.name = name.value;
    const leadId = opportunityStore.selectedOpportunity['Lead'].opportunity.id;
    const associationUrl = `/crm/opportunities/${leadId}/associations`;

    associationEndpoint.setEndpointUrl(associationUrl);
    await associationEndpoint.makeRequest();
    isLoading.value = false;
  };

  const resetState = () => {
    resetForm();
    currentStep.value = 1;
    accounts.value = [];
    contacts.value = [];
    isLoading.value = false;
    notes.value = undefined;
    showFinishScreen.value = false;
    planData.value = {
      solutionsAndStrategy: undefined,
      statedNeeds: undefined,
      campaignObjective: undefined,
      miscNotes: undefined,
    };
    errorBag.value.accounts = [];
    errorBag.value.contacts = [];
  };

  const goBack = () => {
    if (currentStep.value === 1) return;
    currentStep.value -= 1;
  };

  const accountTypesValidForCreation = computed<AccountTypes[]>(() => {
    if (hasReachedMaximumAccounts.value || hasAccountType(accounts.value, 'Advertiser')) {
      return [];
    }

    if (!hasAccountType(accounts.value, 'Advertiser') && !hasAccountType(accounts.value, 'Agency')) {
      return ['Advertiser', 'Agency'];
    }

    if (hasAccountType(accounts.value, 'Agency') && !hasAccountType(accounts.value, 'Advertiser')) {
      return ['Advertiser'];
    }

    return [];
  });

  const isNameStepComplete = computed(() => {
    if (!meta.value.touched || !meta.value.valid) return false;
    return true;
  });

  const isAccountStepComplete = computed(() => !errorBag.value.accounts.length);

  const isContactsStepComplete = computed(() => !errorBag.value.contacts.length);

  const disableAccountStep = computed(() => {
    if (isNameStepComplete.value) return false;
    return true;
  });

  const disableContactStep = computed(() => {
    if (isNameStepComplete.value && isAccountStepComplete.value) return false;
    return true;
  });

  const disableDetailStep = computed(() => {
    if (isNameStepComplete.value && isAccountStepComplete.value && isContactsStepComplete.value) return false;
    return true;
  });

  const goForward = () => {
    if (currentStep.value === 4) return;
    if (currentStep.value + 1 === steps.value.contacts) {
      onGotoContacts();
    }
    currentStep.value += 1;
  };

  const onFinish = () => {
    resetState();
    opportunityStore.showOpportunityCreationModal = false;
  };

  const onSaveClick = async () => {
    const details = detailComponentRef.value.getCurrentState() as OpportunityDetailModel;
    details.planData = planData.value;
    details.opportunity.name = name.value;
    details.opportunity.notes = notes.value;

    await handleSave(details);

    if (opportunityStore.selectedOpportunity['Lead']?.opportunity?.id) {
      resetState();
      onAfterLeadConverted(details.opportunity);
      return;
    }
    onFinish();
  };

  return {
    accounts,
    accountOwnerId,
    accountTypesValidForCreation,
    accountTypesValidForSelection,
    addAccount,
    addContact,
    agencyAccountId,
    canFinish,
    canGoBack,
    canGoToNextStep,
    canSkipStep,
    contacts,
    currentStep,
    detailComponentRef,
    disableAccountStep,
    disableContactStep,
    disableDetailStep,
    errorBag,
    errors,
    goBack,
    goForward,
    hasUnsavedChanges,
    isConvertingLead,
    isLoading,
    loadLeadToConvert,
    name,
    nameProps,
    notes,
    onFinish,
    onSaveClick,
    planData,
    removeAccount,
    removeContact,
    resetState,
    showFinishScreen,
    steps,
    type,
    typeForDetailEditor,
  };
});
